import React, { useState, useEffect } from 'react';
import { User } from '../types';

interface ReferralWidgetProps {
  user: User | null;
}

interface ReferralStats {
  totalReferrals: number;
  activatedReferrals: number;
  rewardedReferrals: number;
  bonusPropertySlots: number;
  maxBonusSlots: number;
  referrals: Array<{
    id: number;
    refereeName: string;
    refereeEmail: string;
    status: 'pending' | 'activated' | 'rewarded';
    createdAt: string;
    updatedAt: string;
  }>;
}

export default function ReferralWidget({ user }: ReferralWidgetProps) {
  const [referralCode, setReferralCode] = useState<string>('');
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (user) {
      loadReferralData();
    }
  }, [user]);

  const loadReferralData = async () => {
    try {
      setLoading(true);
      
      const [codeResponse, statsResponse] = await Promise.all([
        fetch('/api/referral/code', { credentials: 'include' }),
        fetch('/api/referral/stats', { credentials: 'include' })
      ]);

      if (codeResponse.ok && statsResponse.ok) {
        const codeData = await codeResponse.json();
        const statsData = await statsResponse.json();
        
        setReferralCode(codeData.code);
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error loading referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getReferralLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/?ref=${referralCode}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getReferralLink());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  if (!user || user.plan === 'professional' || user.isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  const bonusSlots = stats?.bonusPropertySlots || 0;
  const maxSlots = stats?.maxBonusSlots || 5;
  const progress = (bonusSlots / maxSlots) * 100;
  const remainingSlots = maxSlots - bonusSlots;

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg shadow-sm border border-purple-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            🎁 Earn More Property Slots
            {bonusSlots > 0 && (
              <span className="text-sm font-normal bg-purple-100 text-purple-700 px-2 py-1 rounded">
                +{bonusSlots} earned
              </span>
            )}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Refer friends and unlock up to 5 extra properties for free!
          </p>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between text-sm text-gray-700 mb-2">
          <span>Progress: {bonusSlots} / {maxSlots} bonus slots</span>
          <span className="font-medium">{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        {remainingSlots > 0 && (
          <p className="text-xs text-gray-500 mt-1">
            {remainingSlots} more {remainingSlots === 1 ? 'referral' : 'referrals'} to unlock {remainingSlots} bonus {remainingSlots === 1 ? 'slot' : 'slots'}
          </p>
        )}
      </div>

      <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Your Referral Link
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={getReferralLink()}
            readOnly
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono text-gray-700"
            onClick={(e) => (e.target as HTMLInputElement).select()}
          />
          <button
            onClick={copyToClipboard}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium whitespace-nowrap"
          >
            {copied ? '✓ Copied!' : 'Copy Link'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg p-4 border border-gray-200">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          <span>How it works</span>
          <svg
            className={`w-5 h-5 transform transition-transform ${showDetails ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {showDetails && (
          <div className="mt-4 space-y-3 text-sm text-gray-600">
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-medium">
                1
              </span>
              <p>Share your referral link with friends who need SEO analytics</p>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-medium">
                2
              </span>
              <p>When they sign up and connect their Google Search Console property</p>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-medium">
                3
              </span>
              <p>You both earn +1 property slot (up to 5 bonus slots total)</p>
            </div>
            
            {stats && stats.referrals.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">Your Referrals ({stats.rewardedReferrals})</h4>
                <div className="space-y-2">
                  {stats.referrals.map((referral) => (
                    <div key={referral.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium text-gray-900 text-xs">{referral.refereeName}</p>
                        <p className="text-xs text-gray-500">{new Date(referral.createdAt).toLocaleDateString()}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        referral.status === 'rewarded' 
                          ? 'bg-green-100 text-green-700' 
                          : referral.status === 'activated'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {referral.status === 'rewarded' ? '✓ Rewarded' : 
                         referral.status === 'activated' ? 'Activated' : 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
